import type { FieldConfiguration, ListBuilderColumn } from '../../types';

/**
 * The ONE definition of a cart line's columns, shared by the client cart, the
 * quote cart and the invoice cart (PD-13666).
 *
 * The three cart surfaces render the same thing — an order line being edited —
 * and previously each hand-rolled its own card markup. The shop lists show
 * what happens when three near-identical surfaces each own a full copy of the
 * column definitions: `client-shop/columns.ts`, `quote-shop/columns.ts` and
 * `invoice_shop.json` are independent and have diverged in both size and
 * content. So the cart columns live here once and each surface's assembler at
 * the bottom of this file states its differences explicitly, which makes a
 * divergence a deliberate override rather than something that happens by
 * accident.
 *
 * Cross-list-key inheritance would be the framework answer, but
 * `resolveInheritance` only matches sibling lists inside one key, so plain
 * module sharing is what is available without changing inheritance logic that
 * ~150 lists depend on.
 *
 * FIELD NAMES: cart rows come from `/orders/cart/:clientId`, which returns
 * order_detail rows with the item's display values already flattened onto them
 * (`itemDescription`, `itemManufacturer`, `totalStock`). `objectTable` here is
 * metadata for the field picker and the hydrator — the renderer resolves a
 * value by field NAME off the row — so these read from the flattened names the
 * endpoint actually delivers.
 */

/**
 * Product image, matching the shop lists' image column.
 *
 * The cart's items region is materially narrower than a full-page list — the
 * summary sidebar takes the rest of the width — so every column here is sized
 * tighter than its shop equivalent to keep the editable controls on screen
 * without horizontal scrolling.
 */
export const createCartImageColumn = (): ListBuilderColumn => ({
	id: 'image',
	width: 44,
	fields: [
		{
			id: 'cart_item_image',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'primaryImageUrl',
					objectTable: 'order_detail',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
				imageOptions: { size: 'small', enableViewer: true },
			},
			displayStyle: 'image',
		},
	],
	headers: [{ id: 'cart_image_header', text: '' }],
	sortable: false,
	alignment: 'center',
	verticalAlignment: 'middle',
});

const createLineBreakField = (id: string): FieldConfiguration => ({
	id,
	dataConfig: { type: 'custom_text', customText: '' },
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	displayStyle: 'line_break',
});

/**
 * Item identity: description, manufacturer, NDC and item number — the same
 * four values the cart card rendered in its title block.
 *
 * `cart-item-badges` carries the short-date badge and, on the quote cart, the
 * custom-line marker. It is a component rather than a `badge` field because
 * those flags are derived per line rather than stored on the row, which is the
 * same reason the shop uses `shop-item-attribute-icons`.
 */
export const createCartItemDetailsColumn = (): ListBuilderColumn => ({
	id: 'item_details',
	minWidth: 96,
	// Cells are `white-space: nowrap` table-wide, so without a cap a long
	// drug description sizes this column to its full text and pushes the
	// editable controls out of the cart's narrow items pane.
	maxWidth: 178,
	fields: [
		{
			id: 'cart_item_description',
			// `idField` is load-bearing. The link resolver reads the id off the
			// ROW, and without an explicit field it falls back to `_id` — which
			// on a cart row is the order_detail id, so the link would resolve to
			// /items/<orderDetailId>: a live link to the wrong record. The row's
			// item FK is the item's own id. Same reason order_detail.config.ts
			// pins `idField: 'itemId'` on its item link.
			link: {
				type: 'object',
				enabled: true,
				objectTable: 'item',
				idField: '_fk_item__id',
			},
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'itemDescription',
					objectTable: 'order_detail',
				},
				defaultValue: 'Unknown Item',
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
				// Table cells are `white-space: nowrap`, so without a single-line
				// bound a long drug description sizes this column to its full
				// text and pushes the editable controls out of the cart's narrow
				// items pane. The full description remains available on hover and
				// on the item record the name links to.
				textExpansion: { enabled: true, maxLines: 1 },
			},
			displayStyle: 'text',
		},
		{
			id: 'cart_item_badges',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'custom',
			customComponent: { id: 'cart-item-badges' },
		},
		createLineBreakField('cart_item_details_break_1'),
		{
			id: 'cart_item_manufacturer',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'itemManufacturer',
					objectTable: 'order_detail',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
		},
		createLineBreakField('cart_item_details_break_2'),
		{
			id: 'cart_item_number',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'itemNumber',
					objectTable: 'order_detail',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
		},
		{
			id: 'cart_item_ndc',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'itemNDC',
					objectTable: 'order_detail',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
		},
		// PD-16114 — on a merchandiser drop-off / pick-up line, the unit it
		// moves and the model asked for; absent on other lines.
		{
			...createLineBreakField('cart_item_equipment_break'),
			conditionalVisibility: { field: 'equipmentLabel', condition: 'is_not_null' },
		},
		{
			id: 'cart_item_equipment',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'equipmentLabel',
					objectTable: 'order_detail',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
				// The label carries the unit, its serial and the model; on one
				// line it was cut to "· M…" at 1280px.
				allowTextWrap: true,
			},
			displayStyle: 'text',
			conditionalVisibility: { field: 'equipmentLabel', condition: 'is_not_null' },
		},
		/**
		 * Shipping surcharge label. Not a stored field — it is derived from the
		 * shipping method selected in the cart sidebar and changes whenever the
		 * rep changes it, so it reads live state from `additionalContext`
		 * instead of being merged into the row (which would mean rebuilding
		 * rows, and therefore re-sorting, on every shipping change).
		 */
		{
			id: 'cart_item_surcharge',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'custom',
			customComponent: { id: 'cart-surcharge-label' },
		},
	],
	headers: [
		{ id: 'cart_item_header', text: 'Item' },
		{ id: 'cart_item_header_mfg', text: 'Mfg' },
	],
	sortable: true,
	alignment: 'left',
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'itemDescription',
			objectTable: 'order_detail',
		},
	],
	verticalAlignment: 'top',
});

/**
 * Availability: quantity on hand across the warehouses this client can draw
 * on, and the earliest expiration among them.
 */
export const createCartStockColumn = (): ListBuilderColumn => ({
	id: 'stock',
	width: 84,
	fields: [
		{
			id: 'cart_stock_on_hand',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'totalStock', objectTable: 'order_detail' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
				// Quantities are whole units; the raw value carries the column's
				// scale decimals and would otherwise render as "-2.00".
				formatOptions: { numberFormat: 'decimal', decimalPlaces: 0 },
			},
			displayStyle: 'number',
		},
		createLineBreakField('cart_stock_break'),
		{
			id: 'cart_stock_expiration',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'soonestExpirationDate',
					objectTable: 'order_detail',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'date',
		},
	],
	headers: [
		// Matches the wording used on the item list and the warehouse tabs
		// ("On Hand" / "Next Exp."), rather than inventing a shorter label.
		{ id: 'cart_stock_header', text: 'On Hand' },
		{ id: 'cart_stock_header_exp', text: 'Next Exp.' },
	],
	sortable: true,
	// Right-aligned like the shop's stock column, so the figures line up.
	alignment: 'right',
	sortFields: [
		{
			direction: 'DESC',
			fieldName: 'totalStock',
			objectTable: 'order_detail',
		},
	],
	verticalAlignment: 'top',
});

/**
 * Editable quantity, plus the per-order / daily / monthly limit warning the
 * card rendered beneath its quantity control.
 *
 * `includeLimitWarning` is false for the invoice cart: an invoice line is
 * already billed, so an ordering limit is not a meaningful constraint on it.
 */
export const createCartQuantityColumn = (): ListBuilderColumn => ({
	id: 'quantity',
	// The shop's quantity control is a fixed 112px box; anything narrower
	// clips it.
	width: 124,
	fields: [
		{
			id: 'cart_quantity',
			dataConfig: {
				type: 'field',
				field: { fieldName: '_id', objectTable: 'order_detail' },
			},
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'custom',
			customComponent: { id: 'cart-quantity-input' },
		},
	],
	headers: [{ id: 'cart_quantity_header', text: 'Qty' }],
	sortable: false,
	alignment: 'center',
	verticalAlignment: 'middle',
});

/** Editable unit price. Respects the price-override permission internally. */
export const createCartPriceColumn = (): ListBuilderColumn => ({
	id: 'price',
	width: 84,
	fields: [
		{
			id: 'cart_price',
			dataConfig: {
				type: 'field',
				field: { fieldName: '_id', objectTable: 'order_detail' },
			},
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'custom',
			customComponent: { id: 'cart-price-input' },
		},
	],
	headers: [{ id: 'cart_price_header', text: 'Price' }],
	sortable: false,
	alignment: 'center',
	verticalAlignment: 'middle',
});

/** Line total as the endpoint computed it. */
export const createCartTotalColumn = (): ListBuilderColumn => ({
	id: 'total',
	width: 72,
	fields: [
		{
			id: 'cart_total',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'total', objectTable: 'order_detail' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'currency',
		},
	],
	headers: [{ id: 'cart_total_header', text: 'Total' }],
	sortable: true,
	alignment: 'right',
	sortFields: [
		{ direction: 'DESC', fieldName: 'total', objectTable: 'order_detail' },
	],
	verticalAlignment: 'middle',
});

/** Per-line remove control. */
export const createCartActionsColumn = (): ListBuilderColumn => ({
	id: 'actions',
	width: 44,
	fields: [
		{
			id: 'cart_actions',
			dataConfig: {
				type: 'field',
				field: { fieldName: '_id', objectTable: 'order_detail' },
			},
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'custom',
			customComponent: { id: 'cart-actions-wrapper' },
		},
	],
	headers: [{ id: 'cart_actions_header', text: '' }],
	sortable: false,
	alignment: 'center',
	verticalAlignment: 'middle',
});

/**
 * The strip's columns are the cart's, made DENSER (PD-15805).
 *
 * The strip lives in a 13rem box under the cart, so its budget is a handful of
 * rows, not a page of them. At the cart's own density one suggestion took 68px
 * — set by a 60px product image, with a three-line identity block just behind
 * it — and barely one row was on screen at a time. Every reduction below is
 * about row height:
 *
 * - the image is the smallest size the display offers, so the picture still
 *   identifies the product but no longer sets the row height (the editable
 *   controls beside it do);
 * - identity is the description alone. Manufacturer, item number and NDC are
 *   what a rep checks on the line they are ORDERING, not on a suggestion they
 *   are deciding whether to look at; the description links to the item record
 *   for the rest;
 * - availability is the on-hand figure alone, for the same reason;
 * - and with one value per column the second header line goes too.
 *
 * The cart itself is untouched — it is the thing being edited and keeps its
 * full detail. Everything the strip does share still comes from the cart's own
 * builders (quantity, price), so those cannot drift.
 *
 * Omitted from the cart's set: the line total, which an item that is not in
 * the cart does not have, and the remove action, which would have nothing to
 * act on. Adding is the quantity stepper, exactly as it is on the shop grid:
 * stepping a suggestion off zero puts it in the cart at that quantity, and the
 * price field beside it sets the price it goes in at. The strip then drops the
 * row, because it only ever lists what is NOT already in the cart.
 */
const createCartSuggestionImageColumn = (): ListBuilderColumn => ({
	...createCartImageColumn(),
	width: 36,
	fields: [
		{
			id: 'cart_item_image',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'primaryImageUrl', objectTable: 'order_detail' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
				imageOptions: { size: 'xs', enableViewer: true },
			},
			displayStyle: 'image',
		},
	],
});

const createCartSuggestionItemColumn = (): ListBuilderColumn => {
	const base = createCartItemDetailsColumn();
	const keep = new Set(['cart_item_description', 'cart_item_badges']);

	return {
		...base,
		fields: base.fields.filter((field) => keep.has(field.id)),
		headers: [{ id: 'cart_item_header', text: 'Item' }],
		verticalAlignment: 'middle',
	};
};

const createCartSuggestionStockColumn = (): ListBuilderColumn => {
	const base = createCartStockColumn();

	return {
		...base,
		fields: base.fields.filter((field) => field.id === 'cart_stock_on_hand'),
		headers: [{ id: 'cart_stock_header', text: 'On Hand' }],
		verticalAlignment: 'middle',
	};
};

export const createCartSuggestionColumns = (): Array<ListBuilderColumn> => [
	createCartSuggestionImageColumn(),
	createCartSuggestionItemColumn(),
	createCartSuggestionStockColumn(),
	createCartQuantityColumn(),
	createCartPriceColumn(),
];

/**
 * The default visible columns, which are exactly what the cart card rendered
 * before this change (PD-13666 Q14): image, item identity, availability,
 * quantity, price, line total and the remove control. Anything else a tenant
 * wants — item size, cost — is available in the field picker and switched on
 * per tenant rather than shipped on for everyone.
 */
const createCartBaseColumns = (): Array<ListBuilderColumn> => [
	createCartImageColumn(),
	createCartItemDetailsColumn(),
	createCartStockColumn(),
	createCartQuantityColumn(),
	createCartPriceColumn(),
	createCartTotalColumn(),
	createCartActionsColumn(),
];

/**
 * The three surfaces currently take the identical column set. They stay
 * separate functions because each has its own list key and is expected to
 * diverge by configuration; a difference belongs here, stated explicitly,
 * rather than appearing by accident.
 */
export const createClientCartColumns = (): Array<ListBuilderColumn> =>
	createCartBaseColumns();

// PD-17101 — a quote line's action cell also holds "Log a competitor's
// price", so it is wide enough for two buttons.
export const createQuoteCartColumns = (): Array<ListBuilderColumn> =>
	createCartBaseColumns().map((column) => (column.id === 'actions' ? { ...column, width: 76 } : column));

export const createInvoiceCartColumns = (): Array<ListBuilderColumn> =>
	createCartBaseColumns();
